import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Form, InputNumber, Modal } from 'antd';
import { useUpdateInvoicePartiallyPaidAmount } from 'api/invoices/use-update-invoice-partially-paid-amount.ts';
import { CURRENCY_SIGN } from 'types/currencies.ts';
import type { IInvoiceFactoringFormFields, IInvoiceFactoringModalProps } from './types.ts';

export const InvoiceFactoringModal: React.FC<IInvoiceFactoringModalProps> = (props) => {
  const { invoice, isOpen, onClose } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm<IInvoiceFactoringFormFields>();

  const { mutateAsync: updatePartiallyPaidAmount, isLoading } = useUpdateInvoicePartiallyPaidAmount(invoice.id);

  const hasPartiallyPaidAmount = Boolean(invoice.partiallyPaidAmount);

  useEffect(() => {
    if (isOpen) {
      form.setFieldValue('partiallyPaidAmount', invoice.partiallyPaidAmount || undefined);
    }
  }, [form, invoice.partiallyPaidAmount, isOpen]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: IInvoiceFactoringFormFields) => {
    await updatePartiallyPaidAmount({ partiallyPaidAmount: values.partiallyPaidAmount });
    handleClose();
  };

  const handleClear = async () => {
    await updatePartiallyPaidAmount({ partiallyPaidAmount: null });
    handleClose();
  };

  return (
    <Modal
      title={t('client:factoring')}
      open={isOpen}
      confirmLoading={isLoading}
      okText={t('common:save')}
      cancelText={t('common:cancel')}
      footer={(_, { CancelBtn, OkBtn }) => (
        <Flex justify={hasPartiallyPaidAmount ? 'space-between' : 'flex-end'}>
          {hasPartiallyPaidAmount ? (
            <Button loading={isLoading} danger onClick={handleClear}>
              {t('common:clear')}
            </Button>
          ) : null}
          <Flex gap='small'>
            <CancelBtn />
            <OkBtn />
          </Flex>
        </Flex>
      )}
      onCancel={handleClose}
      onOk={() => form.submit()}>
      <Form form={form} name='update_invoice_partially_paid_amount' layout='vertical' onFinish={handleFinish}>
        <Form.Item
          name='partiallyPaidAmount'
          label={t('invoice:partially_paid')}
          rules={[{ required: true, type: 'number', min: 0 }]}>
          <InputNumber min={0} addonAfter={CURRENCY_SIGN[invoice.currency]} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
